import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articleList } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articleList.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章不存在</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articleList.filter((a) =>
    article.relatedArticles.includes(a.id)
  );

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return String(v);
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishedAt,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '吃瓜爆料网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://chigua.baoliao.wang/article/${article.id}`,
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${article.title} - 吃瓜爆料网`,
    description: article.excerpt,
    url: `https://chigua.baoliao.wang/article/${article.id}`,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <Navbar />
      <main>
        <div className="bg-background-50 border-b border-background-200/60">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article>
          <section className="relative w-full min-h-[400px] md:min-h-[500px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 吃瓜爆料网`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20"></div>
            </div>
            <div className="relative z-10 w-full px-4 md:px-6 pb-10 md:pb-14">
              <div className="max-w-[1280px] mx-auto">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-bold rounded-md">
                    {article.categoryName}
                  </span>
                  {article.isExplosive && (
                    <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-bold rounded-md animate-pulse">
                      爆
                    </span>
                  )}
                </div>
                <h1 className="text-2xl md:text-4xl font-black text-background-50 font-heading max-w-4xl leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 md:gap-4 mt-4 text-xs md:text-sm text-background-50/60">
                  <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                  <span><i className="ri-time-line mr-1"></i>{formatDate(article.publishedAt)}</span>
                  <span><i className="ri-eye-line mr-1"></i>{formatViews(article.views)} 阅读</span>
                  <span><i className="ri-chat-3-line mr-1"></i>{article.comments} 评论</span>
                </div>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
                <div className="flex-1 max-w-[800px]">
                  <div className="bg-background-50 border border-background-200/60 rounded-2xl p-6 md:p-10">
                    <div className="flex items-start gap-4 mb-8 pb-6 border-b border-background-200/60">
                      <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center shrink-0">
                        <i className="ri-user-3-line text-xl text-secondary-500"></i>
                      </div>
                      <div>
                        <p className="font-bold text-foreground-950">{article.author}</p>
                        <p className="text-xs text-foreground-500">吃瓜爆料网 · 特约记者</p>
                      </div>
                    </div>

                    <div
                      className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: article.content_html }}
                    />

                    <div className="mt-8 pt-6 border-t border-background-200/60">
                      <div className="flex flex-wrap gap-2 mb-6">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center gap-3">
                        <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300/60 text-foreground-600 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all cursor-pointer">
                          <i className="ri-share-forward-line"></i>
                        </button>
                        <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300/60 text-foreground-600 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all cursor-pointer">
                          <i className="ri-heart-line"></i>
                        </button>
                        <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300/60 text-foreground-600 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all cursor-pointer">
                          <i className="ri-bookmark-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:w-[320px] shrink-0">
                  <div className="bg-background-100 rounded-2xl p-5 sticky top-20">
                    <h3 className="text-lg font-bold text-foreground-950 font-heading mb-4">
                      作者信息
                    </h3>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                        <i className="ri-user-3-line text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground-950">{article.author}</p>
                        <p className="text-xs text-foreground-500">吃瓜爆料网记者</p>
                      </div>
                    </div>
                    <Link
                      to="/category/mingxing-tafang"
                      className="block w-full text-center px-4 py-2.5 bg-secondary-500 hover:bg-secondary-600 text-background-50 font-medium rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap"
                    >
                      更多猛料 <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-end justify-between mb-8 md:mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-black text-foreground-950 font-heading">
                    更多塌房现场
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">网友正在看</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.length > 0 && (
                  <div className="lg:col-span-2">
                    <ArticleCard
                      id={relatedArticles[0].id}
                      title={relatedArticles[0].title}
                      excerpt={relatedArticles[0].excerpt}
                      categoryName={relatedArticles[0].categoryName}
                      publishedAt={relatedArticles[0].publishedAt}
                      cardImageUrl={relatedArticles[0].cardImageUrl}
                      views={relatedArticles[0].views}
                      comments={relatedArticles[0].comments}
                      isHot={relatedArticles[0].isHot}
                      isExplosive={relatedArticles[0].isExplosive}
                      variant="featured"
                    />
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 md:gap-5">
                  {relatedArticles.slice(1, 5).map((a) => (
                    <ArticleCard
                      key={a.id}
                      id={a.id}
                      title={a.title}
                      excerpt={a.excerpt}
                      categoryName={a.categoryName}
                      publishedAt={a.publishedAt}
                      cardImageUrl={a.cardImageUrl}
                      views={a.views}
                      comments={a.comments}
                      isHot={a.isHot}
                      isExplosive={a.isExplosive}
                      variant="compact"
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}